import React, { useState } from "react";
import styled from "styled-components";

interface CategoryFilterProps {
  categories: string[];
  selectedCategory: string | null;
  onCategorySelect: (category: string | null) => void;
}

const FilterContainer = styled.div`
  width: 100%;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const CategoryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const CategoryButton = styled.button<{ active: boolean }>`
  padding: 0.4rem 0.8rem;
  border: none;
  background: transparent;
  color: ${(props) => (props.active ? "#e97d7d" : "#666")};
  border-radius: 0.3rem;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  text-decoration: ${(props) => (props.active ? "underline" : "none")};
  text-underline-offset: 3px;
  text-decoration-thickness: 1.5px;

  &:hover {
    color: #e97d7d;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1.5px;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    text-decoration: ${(props) => (props.active ? "underline" : "none")};
    text-underline-offset: 3px;
    text-decoration-thickness: 1.5px;

    &:hover {
      color: #e97d7d;
      text-decoration: underline;
      text-underline-offset: 3px;
      text-decoration-thickness: 1.5px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }
`;

const ClearButton = styled.button`
  padding: 0.4rem 0.8rem;
  border: none;
  background: transparent;
  color: #666;
  border-radius: 0.3rem;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  text-decoration: none;

  &:hover {
    color: #e97d7d;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1.5px;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    text-decoration: none;

    &:hover {
      color: #e97d7d;
      text-decoration: underline;
      text-underline-offset: 3px;
      text-decoration-thickness: 1.5px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }
`;

const MobileDropdown = styled.div`
  display: none;

  @media (max-width: 480px) {
    display: block;
    position: relative;
  }
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: 2px solid #ddd;
  background: #fff;
  color: #333;
  border-radius: 0.8rem;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #e97d7d;
    background: #f8f8f8;
  }
`;

const DropdownIcon = styled.span<{ open: boolean }>`
  transform: ${(props) => (props.open ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.3s ease;
  font-size: 1.2rem;
`;

const DropdownList = styled.div<{ open: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 2px solid #ddd;
  border-top: none;
  border-radius: 0 0 0.8rem 0.8rem;
  max-height: ${(props) => (props.open ? "300px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.button<{ active: boolean }>`
  width: 100%;
  padding: 1rem;
  border: none;
  background: ${(props) => (props.active ? "#e97d7d" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#333")};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  &:hover {
    background: ${(props) => (props.active ? "#e97d7d" : "#f8f8f8")};
  }

  &:first-child {
    border-radius: 0;
  }
`;

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  selectedCategory,
  onCategorySelect,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleCategoryClick = (category: string | null) => {
    onCategorySelect(category);
    setIsDropdownOpen(false);
  };

  const getDisplayText = () => {
    if (!selectedCategory) return "All Categories";
    return selectedCategory;
  };

  return (
    <FilterContainer>
      <CategoryList>
        <ClearButton
          onClick={() => handleCategoryClick(null)}
          style={{
            color: selectedCategory === null ? "#e97d7d" : "#666",
            textDecoration: selectedCategory === null ? "underline" : "none",
          }}
        >
          All Categories
        </ClearButton>
        {categories.map((category) => (
          <CategoryButton
            key={category}
            active={selectedCategory === category}
            onClick={() => handleCategoryClick(category)}
          >
            {category}
          </CategoryButton>
        ))}
      </CategoryList>

      {/* Mobile Dropdown */}
    </FilterContainer>
  );
};

export default CategoryFilter;
